/**
 * Header v2 Navigation Component
 * Handles mobile drawer, desktop dropdowns, language switcher, and interactions
 */

(function() {
  'use strict';

  class HeaderV2 {
    constructor(container) {
      this.container = container;
      this.isMobile = window.matchMedia('(max-width: 991px)').matches;
      this.mobileQuery = window.matchMedia('(max-width: 991px)');
      this.dropdownCloseTimeout = null;
      this.currentDropdown = null;
      this.scrollDirection = 'up';
      this.lastScrollTop = 0;
      this.scrollThreshold = 10;
      this.priorityLinksObserver = null;

      this.init();
    }

    init() {
      // Responsive initialization
      this.handleResponsive();
      this.mobileQuery.addListener(() => this.handleResponsive());

      // Common features
      this.initLanguageSwitcher();
      this.initQuickLinksDropdown();
      this.initMiniCart();
      this.initPriorityLinks();

      // Close dropdowns on outside click
      document.addEventListener('click', (e) => this.handleOutsideClick(e));
    }

    handleResponsive() {
      this.isMobile = this.mobileQuery.matches;

      if (this.isMobile) {
        this.initMobileNavigation();
        this.initScrollBehavior();
        // Disconnect observer — action area is hidden on mobile
        if (this.priorityLinksObserver) {
          this.priorityLinksObserver.disconnect();
          this.priorityLinksObserver = null;
        }
        const menu = this.container.querySelector('.header-v2-menu');
        if (menu) menu.classList.remove('header-v2-compact');
      } else {
        this.initDesktopDropdowns();
        this.cleanupMobileNavigation();
        this.initPriorityLinks(); // re-attach observer on desktop
      }
    }

    /* ==========================================================================
       Mobile Navigation
       ========================================================================== */

    initMobileNavigation() {
      const hamburger = this.container.querySelector('.header-v2-hamburger-btn');
      const overlay = this.container.querySelector('.header-v2-mobile-overlay');
      const menuBtns = this.container.querySelectorAll('[data-target-screen-name]');

      if (hamburger) {
        hamburger.addEventListener('click', () => this.toggleMobileMenu());
      }

      if (overlay) {
        overlay.addEventListener('click', () => this.closeMobileMenu());
      }

      menuBtns.forEach(btn => {
        btn.addEventListener('click', (e) => {
          const targetScreen = btn.getAttribute('data-target-screen-name');
          if (targetScreen) {
            e.preventDefault();
            if (!document.body.classList.contains('header-v2-mobile-menu-visible') &&
                !document.body.classList.contains('header-v2-dropdown-only')) {
              this.openDropdownDirect();
            }
            this.showChildNav(targetScreen);
          }
        });
      });

      this.container.addEventListener('click', (e) => {
        if (e.target.closest('.header-v2-mobile-back-btn')) {
          this.navigateBack();
        }
      });
    }

    toggleMobileMenu() {
      const isVisible = document.body.classList.contains('header-v2-mobile-menu-visible');

      if (isVisible) {
        this.closeMobileMenu();
      } else {
        this.openMobileMenu();
      }
    }

    openMobileMenu() {
      document.body.classList.add('header-v2-mobile-menu-visible', 'no-scroll');
      this.navigationStack = ['home'];
    }

    openDropdownDirect() {
      document.body.classList.add('header-v2-dropdown-only', 'no-scroll');
      this.navigationStack = ['home'];  // always reset; prior [] would break back-nav
    }

    closeMobileMenu() {
      document.body.classList.remove('header-v2-mobile-menu-visible', 'header-v2-dropdown-only', 'no-scroll');

      // Close all sub-screens
      const dropdowns = this.container.querySelectorAll('.header-v2-menu-dropdown-container');
      dropdowns.forEach(dropdown => {
        dropdown.classList.remove('header-v2-dropdown-visible');
      });

      // Reset navigation stack
      this.navigationStack = [];
    }

    showChildNav(targetScreen) {
      const dropdown = this.container.querySelector(`.header-v2-menu-dropdown-container[data-dropdown-name="${targetScreen}"]`);

      if (dropdown) {
        dropdown.classList.add('header-v2-dropdown-visible');

        // Update navigation stack
        if (!this.navigationStack || this.navigationStack.length === 0) {
          this.navigationStack = ['home'];
        }
        this.navigationStack.push(targetScreen);
      }
    }

    navigateBack() {
      if (!this.navigationStack || this.navigationStack.length <= 1) {
        return;
      }

      // Remove current screen from stack
      const currentScreen = this.navigationStack.pop();

      // Close current dropdown
      const currentDropdown = this.container.querySelector(`.header-v2-menu-dropdown-container[data-dropdown-name="${currentScreen}"]`);
      if (currentDropdown) {
        currentDropdown.classList.remove('header-v2-dropdown-visible');
      }

      // If back to home, close entirely in dropdown-only mode
      if (this.navigationStack.length === 1) {
        if (document.body.classList.contains('header-v2-dropdown-only')) {
          this.closeMobileMenu();
        }
      }
    }

    cleanupMobileNavigation() {
      // Remove mobile classes if switching from mobile to desktop
      document.body.classList.remove('header-v2-mobile-menu-visible', 'header-v2-dropdown-only', 'no-scroll');

      const dropdowns = this.container.querySelectorAll('.header-v2-menu-dropdown-container');
      dropdowns.forEach(dropdown => {
        dropdown.classList.remove('header-v2-dropdown-visible');
      });
    }

    /* ==========================================================================
       Desktop Dropdowns
       ========================================================================== */

    initDesktopDropdowns() {
      const menuBtns = this.container.querySelectorAll('.header-v2-menu-btn[data-dropdown-name]');

      menuBtns.forEach(btn => {
        const dropdownName = btn.getAttribute('data-dropdown-name');
        const dropdown = this.container.querySelector(`.header-v2-menu-dropdown-container[data-dropdown-name="${dropdownName}"]`);

        if (dropdown) {
          // Mouse enter - show dropdown
          btn.addEventListener('mouseenter', () => {
            this.showDesktopDropdown(dropdownName, btn);
          });

          // Mouse leave - delay hide
          btn.addEventListener('mouseleave', () => {
            this.scheduleDropdownClose();
          });

          dropdown.addEventListener('mouseenter', () => {
            this.cancelDropdownClose();
          });

          dropdown.addEventListener('mouseleave', () => {
            this.scheduleDropdownClose();
          });
        }
      });
    }

    showDesktopDropdown(dropdownName, btn) {
      // Cancel any pending close
      this.cancelDropdownClose();

      // Close other dropdowns
      if (this.currentDropdown && this.currentDropdown !== dropdownName) {
        this.hideDesktopDropdown(this.currentDropdown);
      }

      const dropdown = this.container.querySelector(`.header-v2-menu-dropdown-container[data-dropdown-name="${dropdownName}"]`);

      if (dropdown) {
        dropdown.classList.add('header-v2-dropdown-visible');
        this.container.classList.add('header-dropdown-opened');
        btn.classList.add('jsc-active');
        this.currentDropdown = dropdownName;
      }
    }

    hideDesktopDropdown(dropdownName) {
      const dropdown = this.container.querySelector(`.header-v2-menu-dropdown-container[data-dropdown-name="${dropdownName}"]`);
      const btn = this.container.querySelector(`.header-v2-menu-btn[data-dropdown-name="${dropdownName}"]`);

      if (dropdown) {
        dropdown.classList.remove('header-v2-dropdown-visible');
      }

      if (btn) {
        btn.classList.remove('jsc-active');
      }

      this.container.classList.remove('header-dropdown-opened');
      this.currentDropdown = null;
    }

    scheduleDropdownClose() {
      this.dropdownCloseTimeout = setTimeout(() => {
        if (this.currentDropdown) {
          this.hideDesktopDropdown(this.currentDropdown);
        }
      }, 500); // 500ms delay
    }

    cancelDropdownClose() {
      if (this.dropdownCloseTimeout) {
        clearTimeout(this.dropdownCloseTimeout);
        this.dropdownCloseTimeout = null;
      }
    }

    /* ==========================================================================
       Language Switcher
       ========================================================================== */

    initLanguageSwitcher() {
      const toggle = this.container.querySelector('.header-v2-language-toggle');
      const switcher = this.container.querySelector('.header-v2-language-switcher');
      const options = this.container.querySelectorAll('.header-v2-language-option');

      if (toggle && switcher) {
        toggle.addEventListener('click', (e) => {
          e.stopPropagation();
          switcher.classList.toggle('jsc-open');
        });
      }

      options.forEach(option => {
        option.addEventListener('click', (e) => {
          e.preventDefault();
          const locale = option.getAttribute('data-locale');
          if (locale) {
            this.switchLocale(locale);
          }
        });
      });

      // Mobile language buttons
      const mobileBtns = this.container.querySelectorAll('.header-v2-mobile-language-btn');
      mobileBtns.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const locale = btn.getAttribute('data-locale');
          if (locale) {
            this.switchLocale(locale);
          }
        });
      });
    }

    switchLocale(locale) {
      // Get current path
      let currentPath = window.location.pathname;

      // Remove current locale from path if present
      const localePattern = /^\/[a-z]{2}(-[A-Z]{2})?(\/|$)/;
      const match = currentPath.match(localePattern);

      if (match) {
        currentPath = currentPath.replace(match[0], '/');
      }

      // Build new path with selected locale
      let newPath;
      if (locale === this.getDefaultLocale()) {
        // Default locale - no prefix
        newPath = currentPath;
      } else {
        // Add locale prefix
        newPath = `/${locale}${currentPath}`;
      }

      // Preserve query string
      const queryString = window.location.search;
      const hash = window.location.hash;

      window.location.href = newPath + queryString + hash;
    }

    getDefaultLocale() {
      // Read from data attribute or assume 'en'
      const defaultLocale = this.container.getAttribute('data-default-locale');
      return defaultLocale || 'en';
    }

    /* ==========================================================================
       Quick Links Dropdown
       ========================================================================== */

    /**
     * Initialize quick links dropdown toggle
     * Always shows as dropdown (no overflow detection)
     */
    initQuickLinksDropdown() {
      if (this.isMobile) return;

      const container = this.container.querySelector('.header-v2-quick-links-container');
      const toggle = this.container.querySelector('.header-v2-quick-links-toggle');

      if (!container || !toggle) return;

      // Toggle dropdown on button click
      toggle.addEventListener('click', (e) => {
        e.stopPropagation();
        const isOpen = container.classList.contains('jsc-open');

        if (isOpen) {
          container.classList.remove('jsc-open');
          toggle.setAttribute('aria-expanded', 'false');
        } else {
          container.classList.add('jsc-open');
          toggle.setAttribute('aria-expanded', 'true');
        }
      });
    }

    /* ==========================================================================
       Priority Inline Links — overflow-aware adaptive display
       ========================================================================== */

    initPriorityLinks() {
      const menu = this.container.querySelector('.header-v2-menu');
      const priorityLinks = this.container.querySelector('.header-v2-priority-links');

      if (!menu || !priorityLinks) return;

      const checkOverflow = () => {
        // Remove compact class to measure true required width (priority links in DOM)
        menu.classList.remove('header-v2-compact');
        void menu.offsetWidth; // force synchronous reflow before measuring

        const isOverflowing = menu.scrollWidth > menu.clientWidth;
        if (isOverflowing) {
          menu.classList.add('header-v2-compact');
        }

        // Reveal after first measurement (removes jsc-invisible FOUC guard)
        priorityLinks.classList.remove('jsc-invisible');
      };

      if (this.priorityLinksObserver) {
        this.priorityLinksObserver.disconnect();
      }

      this.priorityLinksObserver = new ResizeObserver(checkOverflow);
      this.priorityLinksObserver.observe(menu);
      checkOverflow();
    }

    /* ==========================================================================
       Scroll Behavior (Mobile header hide/show)
       ========================================================================== */

    initScrollBehavior() {
      let ticking = false;

      window.addEventListener('scroll', () => {
        if (!ticking) {
          window.requestAnimationFrame(() => {
            this.handleScroll();
            ticking = false;
          });
          ticking = true;
        }
      });
    }

    handleScroll() {
      const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;

      if (Math.abs(currentScrollTop - this.lastScrollTop) < this.scrollThreshold) {
        return;
      }

      if (currentScrollTop > this.lastScrollTop && currentScrollTop > 100) {
        // Scrolling down
        if (this.scrollDirection !== 'down') {
          this.scrollDirection = 'down';
          document.documentElement.setAttribute('data-scrolldir', 'down');
        }
      } else if (currentScrollTop < this.lastScrollTop) {
        // Scrolling up
        if (this.scrollDirection !== 'up') {
          this.scrollDirection = 'up';
          document.documentElement.setAttribute('data-scrolldir', 'up');
        }
      }

      this.lastScrollTop = currentScrollTop <= 0 ? 0 : currentScrollTop;
    }

    /* ==========================================================================
       Mini Cart Integration
       ========================================================================== */

    initMiniCart() {
      // Listen to theme cart events
      document.addEventListener('theme:cart:change', (event) => {
        this.updateCartCounter(event.detail.cart);
      });

      document.addEventListener('theme:cart:init', () => {
        this.fetchCartAndUpdate();
      });

      // Initial cart fetch
      this.fetchCartAndUpdate();
    }

    updateCartCounter(cart) {
      const counters = this.container.querySelectorAll('.v2-cart-counter');

      counters.forEach(counter => {
        if (cart.item_count > 0) {
          counter.textContent = cart.item_count;
          counter.style.display = 'flex';
        } else {
          counter.style.display = 'none';
        }
      });
    }

    fetchCartAndUpdate() {
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          this.updateCartCounter(cart);
        })
        .catch(error => {
          console.error('Failed to fetch cart:', error);
        });
    }

    /* ==========================================================================
       Outside Click Handler
       ========================================================================== */

    /**
     * Close dropdowns when clicking outside
     */
    handleOutsideClick(e) {
      // Close language switcher
      const languageSwitcher = this.container.querySelector('.header-v2-language-switcher');
      if (languageSwitcher && !languageSwitcher.contains(e.target)) {
        languageSwitcher.classList.remove('jsc-open');
      }

      // Close quick links dropdown
      const quickLinksContainer = this.container.querySelector('.header-v2-quick-links-container');
      if (quickLinksContainer &&
          quickLinksContainer.classList.contains('jsc-open') &&
          !quickLinksContainer.contains(e.target)) {
        quickLinksContainer.classList.remove('jsc-open');

        const toggle = quickLinksContainer.querySelector('.header-v2-quick-links-toggle');
        if (toggle) {
          toggle.setAttribute('aria-expanded', 'false');
        }
      }
    }
  }

  /* ==========================================================================
     Initialize on DOM Ready
     ========================================================================== */

  function initHeaderV2() {
    const header = document.getElementById('header-v2');
    if (header && !header.headerV2Instance) {
      header.headerV2Instance = new HeaderV2(header);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHeaderV2);
  } else {
    initHeaderV2();
  }

  // Re-initialize on Shopify section events (theme editor)
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector('#header-v2')) {
      initHeaderV2();
    }
  });

})();
